import type { ZodObject, ZodType } from 'zod';
import type { UserPermission } from '../schema/User/UserPermission';
import { analysisRoutes } from './analysis.routes';
import { authRoutes } from './auth.routes';
import { companiesRoutes } from './companies.routes';
import { documentsRoutes } from './documents.routes';
import { laboratoriesRoutes } from './laboratories.routes';
import { mascaradeRoutes } from './mascarade.routes';
import { matricesRoutes } from './matrices.routes';
import { noticesRoutes } from './notices.routes';
import { notificationsRoutes } from './notifications.routes';
import { prescriptionsRoutes } from './prescriptions.routes';
import { programmingPlanDomainsRoutes } from './programmingPlanDomains.routes';
import { programmingPlansRoutes } from './programmingPlans.routes';
import { sachaRoutes } from './sacha.routes';
import { samplesRoutes } from './samples.routes';
import { specificDataFieldsRoutes } from './specificDataFields.routes';
import { usersRoutes } from './users.routes';

export const MaestroRoutes = [
  '/analysis-dai',
  '/analysis-dai/:analysisDaiId/mark-error',
  '/analysis-rai',
  '/analysis-rai/:analysisRaiId/replay',
  '/analysis',
  '/analysis/:analysisId',
  '/analysis/:analysisId/reportDocuments',
  '/auth',
  '/auth/role',
  '/auth/logout',
  '/auth/redirect-url',
  '/companies',
  '/companies/:siret/workshops',
  '/documents/resources',
  '/documents/resources/:documentId',
  '/documents/resources/:documentId/download',
  '/documents/upload-signed-url',
  '/laboratories',
  '/laboratories/agreements',
  '/laboratories/agreements/checks',
  '/laboratories/agreements/export',
  '/laboratories/:laboratoryId',
  '/laboratories/:laboratoryId/config',
  '/laboratories/:laboratoryId/agreements',
  '/laboratories/:laboratoryId/residue-mappings',
  '/mascarade/:userId',
  '/mascarade',
  '/matrices',
  '/notifications',
  '/notifications/:notificationId',
  '/notices/:type',
  '/prescriptions',
  '/prescriptions/counts',
  '/prescriptions/export',
  '/prescriptions/import',
  '/prescriptions/save',
  '/prescriptions/regions',
  '/prescriptions/regions/changes-viewed',
  '/prescriptions/:prescriptionId/regions/:region/comments',
  '/prescriptions/:prescriptionId/regions/:region',
  '/prescriptions/:prescriptionId/regions/:region/departments/:department/comments',
  '/prescriptions/:prescriptionId/regions/:region/departments/:department',
  '/prescriptions/:prescriptionId/regions/:region/departments/:department/companies/:companySiret',
  '/prescriptions/:prescriptionId',
  '/programming-plan-domains',
  '/programming-plan-domains/campaigns',
  '/programming-plan-domains/:programmingPlanDomainId',
  '/programming-plan-domains/:programmingPlanDomainId/duplicate',
  '/programming-plans/:programmingPlanId/duplicate',
  '/programming-plans/:programmingPlanId/sub-plans',
  '/programming-plans/:programmingPlanId/sub-plans/:programmingSubPlanId',
  '/programming-plans/:programmingPlanId/sub-plans/:programmingSubPlanId/duplicate',
  '/programming-plans/:programmingPlanId/sub-plans/:programmingSubPlanId/settings',
  '/programming-plans/:programmingPlanId/sub-plans/:programmingSubPlanId/specific-data-fields',
  '/programming-plans',
  '/programming-plans/send-to-regions',
  '/programming-plans/launch-campaign',
  '/programming-plans/send-to-departments',
  '/programming-plans/send-to-samplers',
  '/programming-plans/:programmingPlanId',
  '/programming-plans/:programmingPlanId/local-status',
  '/programming-plans/:programmingPlanId/settings',
  '/sacha/commemoratifs',
  '/sacha/contexts',
  '/sacha/referentials',
  '/samples',
  '/samples/count',
  '/samples/export',
  '/samples/compliance-stats',
  '/samples/residue-stats',
  '/samples/:sampleId/compliance',
  '/samples/:sampleId/document',
  '/samples/:sampleId/documents',
  '/samples/:sampleId/documents/:documentId',
  '/samples/:sampleId/documents/:documentId/download',
  '/samples/:sampleId/emptyForm',
  '/samples/:sampleId/items/:itemNumber/copy/:copyNumber/document',
  '/samples/:sampleId/items/:itemNumber/copy/:copyNumber',
  '/samples/:sampleId',
  '/specific-data-fields',
  '/specific-data-fields/:fieldId',
  '/specific-data-fields/:fieldId/options',
  '/specific-data-fields/:fieldId/options/:optionId',
  '/specific-data-fields/attribute',
  '/specific-data-fields/attribute/value',
  '/users',
  '/users/:userId',
  '/users/:userId/certification'
] as const;

type Routes = typeof analysisRoutes &
  typeof authRoutes &
  typeof companiesRoutes &
  typeof documentsRoutes &
  typeof laboratoriesRoutes &
  typeof mascaradeRoutes &
  typeof matricesRoutes &
  typeof noticesRoutes &
  typeof notificationsRoutes &
  typeof prescriptionsRoutes &
  typeof programmingPlanDomainsRoutes &
  typeof programmingPlansRoutes &
  typeof sachaRoutes &
  typeof samplesRoutes &
  typeof specificDataFieldsRoutes &
  typeof usersRoutes;

export const routes: Routes = {
  ...analysisRoutes,
  ...authRoutes,
  ...companiesRoutes,
  ...documentsRoutes,
  ...laboratoriesRoutes,
  ...mascaradeRoutes,
  ...matricesRoutes,
  ...noticesRoutes,
  ...notificationsRoutes,
  ...prescriptionsRoutes,
  ...programmingPlanDomainsRoutes,
  ...programmingPlansRoutes,
  ...sachaRoutes,
  ...samplesRoutes,
  ...specificDataFieldsRoutes,
  ...usersRoutes
} satisfies {
  [path in MaestroRoutes]: { [method in RouteMethod]?: ToRoute } & {
    params?: ZodUrlParams<path>;
  };
};

type MaestroRouteMethodIsUnprotected<
  R extends MaestroRoutes,
  M extends keyof (typeof routes)[R]
> = (typeof routes)[R][M] extends { unprotected: true } ? true : false;

type PickByValue<Obj, Condition> = FromEntries<
  Extract<Entries<Obj>, [any, Condition]>
>;

type Entries<Obj> = {
  [K in keyof Obj]: [K, Obj[K]];
}[keyof Obj];

type FromEntries<Entries extends [any, any]> = {
  [Entry in Entries as Entry[0]]: Entry[1];
};

export type MaestroRouteUnprotectedMethod<T extends MaestroRoutes> =
  keyof PickByValue<
    {
      [K in keyof (typeof routes)[T]]: MaestroRouteMethodIsUnprotected<T, K>;
    },
    true
  >;

export type MaestroRouteProtectedMethod<T extends MaestroRoutes> = Exclude<
  keyof (typeof routes)[T],
  MaestroRouteUnprotectedMethod<T> | 'params'
>;

type MaestroRouteHasUnprotectedMethod<T> = T extends MaestroRoutes
  ? MaestroRouteUnprotectedMethod<T> extends never
    ? false
    : true
  : false;

type MaestroRouteHasProtectedMethod<T> = T extends MaestroRoutes
  ? MaestroRouteProtectedMethod<T> extends never
    ? false
    : true
  : false;

export type MaestroRoutes = (typeof MaestroRoutes)[number];

export type ProtectedRoutes = {
  [K in MaestroRoutes]: MaestroRouteHasProtectedMethod<K> extends true
    ? K
    : never;
}[MaestroRoutes];

export type UnprotectedRoutes = {
  [K in MaestroRoutes]: MaestroRouteHasUnprotectedMethod<K> extends true
    ? K
    : never;
}[MaestroRoutes];

export type ToRoute = {
  query?: ZodObject;
  body?: ZodType;
  response: ZodType;
  skipSanitization?: true;
} & (
  | {
      unprotected: true;
    }
  | { permissions: [UserPermission, ...UserPermission[]] | 'NONE' }
  | { accountPermissions: [UserPermission, ...UserPermission[]] }
);

type ZodParseUrlParams<url> = url extends `${infer start}/${infer rest}`
  ? ZodParseUrlParams<start> & ZodParseUrlParams<rest>
  : url extends `:${infer param}`
    ? { [k in param]: ZodType }
    : Record<never, never>;

type ZodUrlParams<url, Z = ZodParseUrlParams<url>> = keyof Z extends never
  ? undefined
  : Z;

export type RouteMethod = 'get' | 'post' | 'put' | 'delete';

export type SubRoutes<T extends MaestroRoutes> = {
  [path in Extract<MaestroRoutes, `${T}${string}`>]: {
    [method in RouteMethod]?: ToRoute;
  } & {
    params?: ZodUrlParams<path>;
  };
};
